/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import classNames from 'classnames';

import { get as getUser } from 'js/lib/user';

import { breakpoints } from '@coursera/cds-core';

import { ProgressiveLoader } from 'bundles/ai-coach-platform/components/building-blocks';
import {
  ChatMessageBody,
  ChatMessageContainer,
  ChatMessageHeader,
} from 'bundles/ai-coach-platform/components/patterns/chat/common';
import { ChatMessage, type ChatMessageProps } from 'bundles/ai-coach-platform/components/patterns/chat/common';
import ChatEmptyState from 'bundles/ai-coach-platform/components/patterns/chat/common/ChatEmptyState';
import type { CoachPersona, SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import {
  getCoachPersona,
  getLearnerPersona,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/personaUtils';
import leeCoachEpicClient from 'bundles/epic/clients/leeCoach';

import _t from 'i18n!nls/ai-coach-platform';

import type { GlobalState, MessageTypes } from './store/types';
import { isClientMessage as isClientMessageUtil, isCoachMessage as isCoachMessageUtil } from './utils/messageUtils';

export type HideMessageType = ({
  message,
  index,
  messages,
}: {
  message: MessageTypes;
  index: number;
  messages: Array<MessageTypes>;
}) => boolean;

export type Props = {
  profileClientAvatar?: string;
  sendMessage: SendMessageFunc;
  emptyState?: React.ReactElement;

  renderCustomMessage?: (props: ChatMessageProps) => React.ReactElement | null;

  /**
   * Hides the message in the chat. This function accepts an individual message and its index
   * returns a boolean value if the message needs to be hidden based on the function's implementation
   * at the parent level.
   * Eg: Hiding the first "initiation" message and chat summary message for coach item use case.
   */
  hideMessage?: HideMessageType;
  activeChatSessionId?: string;
  coachPersona?: CoachPersona;
  forceHideMessageLoader?: boolean;
};

const styles = {
  messagesContainer: css`
    padding: 0 var(--cds-spacing-200);

    /* Hide scrollbar for Chrome, Safari and Opera */

    ::-webkit-scrollbar {
      display: none;
    }

    height: 100%;
    max-height: none;

    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    ${breakpoints.down('xs')} {
      height: 100%;
      max-height: none;
    }
  `,
  message: css`
    margin: var(--cds-spacing-200) 0;
    word-wrap: break-word;

    svg {
      flex-shrink: 0;
    }

    &.coach-message-response {
      scroll-margin-top: var(--cds-spacing-150);
    }

    &:focus {
      outline: none;
    }

    .coach-chat-message-body {
      /* aligns body with start of sender name */
      margin-inline-start: 40px;
      margin-top: calc(-1 * var(--cds-spacing-100));
    }
  `,
};

const ChatMessages = ({
  profileClientAvatar,
  emptyState,
  hideMessage,
  sendMessage,
  activeChatSessionId: chatSessionId,
  renderCustomMessage = () => null,
  coachPersona = getCoachPersona(),
  forceHideMessageLoader,
}: Props) => {
  const { messages, showMessageLoader, activeChatSessionId } = useSelector((state: GlobalState) => ({
    messages: (chatSessionId && state.messages.messagesBySessionId[chatSessionId]) || state.messages.messages,
    showMessageLoader: state.behavior.messageLoader,
    activeChatSessionId: state.sessionState?.chatSessionId,
  }));
  const useStreaming = leeCoachEpicClient.get('enableTextStreaming');

  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const messageCount = messages.length;

  // scroll latest message into view every time `messageCount` updates i.e. new message is added
  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView?.({ behavior: 'smooth' });
    }
  }, [messageCount]);

  const { id: userId } = getUser();

  const learnerPersona = getLearnerPersona(profileClientAvatar);

  const showCoachLoader =
    !forceHideMessageLoader && showMessageLoader && (activeChatSessionId === chatSessionId || !chatSessionId);

  const emptyStateRenderer = () => {
    return emptyState ?? <ChatEmptyState />;
  };

  const isEmptyState = !messages?.length;

  if (isEmptyState) {
    return emptyStateRenderer();
  }

  return (
    // a11y implementation: https://www.w3.org/WAI/WCAG21/working-examples/aria-role-log/chatlog.html
    // see COACH-1236
    <div role="log">
      <div
        data-testid="ai-coach-chat-messages"
        ref={messagesContainerRef}
        css={styles.messagesContainer}
        aria-label={_t('Coach chat messages')}
        role="list"
      >
        {messages?.map((message, index) => {
          const { timestamp, sender } = message;

          const isLastMessage = index === messages.length - 1;

          const isCoachMessage = isCoachMessageUtil(sender);
          const isClientMessage = isClientMessageUtil(sender);

          if (hideMessage && hideMessage({ message, index, messages })) {
            return null;
          }

          const messageProps: ChatMessageProps = {
            message,
            index,
            messages,
            sendMessage,
            learnerPersona,
            coachPersona,
          };

          return (
            <ChatMessageContainer
              key={isCoachMessage ? message.customId : `${userId}-${timestamp.valueOf()}`}
              className={classNames({
                'coach-message-client': isClientMessage,
                'coach-message-response': isCoachMessage,
              })}
              ref={isLastMessage ? lastMessageRef : null}
              css={styles.message}
              role="listitem"
            >
              {renderCustomMessage(messageProps) ?? <ChatMessage {...messageProps} showCoachLoader={showCoachLoader} />}
            </ChatMessageContainer>
          );
        })}

        {!useStreaming && showCoachLoader && (
          <ChatMessageContainer role="status" aria-live="polite" css={styles.message}>
            <ChatMessageHeader senderName={coachPersona.name} senderAvatar={coachPersona.avatar} />
            <ChatMessageBody className="coach-chat-message-body">
              <ProgressiveLoader action={messages[messages.length - 1]?.action} />
            </ChatMessageBody>
          </ChatMessageContainer>
        )}
      </div>
    </div>
  );
};

export default ChatMessages;
